/**
 * Text 配置用の独立した滑らかな path を構築する pure モジュール
 * （happa v1.1 Phase C.1 Step 3 追加）。
 *
 * 背景:
 * Phase C.1 Step 2 までは silhouette（warp 後の innerPoly を `offsetPolygonCurve`
 * に通したもの）をそのままテキスト経路として使用していた。しかしユーザ診断
 * 「silhouette の凹凸に追従しすぎてテキストが読みづらい / 追従度を連続的に
 * 制御したい」により、silhouette とは **独立した text 用 path** を構築する
 * 責務を本モジュールに切り出す。
 *
 * 本モジュールは silhouette に対し以下の変換を行い、**視覚 CW 巻きの閉曲線**
 * を返す:
 *
 * 1. 退化ガード（頂点数 / 入力値 / smoothness の正規化）
 * 2. 平均辺長と凸包の計算
 * 3. 追従度 `followDegree`（0..100）に応じて **制御点列 `controls`** を決定
 *    - 0   → 凸包のみ（最も緩い追従、テキスト読みやすさ最優先）
 *    - 100 → silhouette そのまま（最も強い追従）
 *    - 中間 → RDP 簡略化 + 凸包境界からの深さクランプ
 * 4. Centripetal Catmull-Rom spline（alpha=0.5）で閉曲線を滑らかに補間
 * 5. 巻き向き保証（signedArea < 0 なら reverse、視覚 CW を担保）
 *
 * **なぜ centripetal (alpha=0.5) か**:
 * 標準 Catmull-Rom (alpha=0) は制御点が密集した箇所で自己ループを作りやすい。
 * centripetal は knot を距離の平方根で重み付けするため、凸包頂点が不均等に
 * 並ぶ本用途でも自己交差を作らず、視覚的にも「元の形を保ちつつ丸める」
 * 直感と合致する。
 *
 * **巻き向き規約**:
 * 本プロジェクトの `generateInitialPolygon` / `offsetPolygonCurve` は
 * **視覚 CW（signedArea > 0、Y-down 画面で時計回り）** を出力する。
 * `buildTextBasePath` もこれに合わせて **視覚 CW** を返す。`silhouette.ts`
 * の `signedArea` と符号規約は完全一致。
 *
 * 設計参照:
 * - `docs/happa-designtool.md` §5.4.1（テキスト経路構築）
 * - Phase C.1 Step 3 計画（ユーザ診断: silhouette / text path 分離方針）
 *
 * 依存: `./types` の `Vec2` のみ。DOM / Canvas / RNG 参照なし、pure 関数群。
 */

import type { Vec2 } from './types';

/**
 * `smoothness` 省略時のデフォルト値。
 *
 * 8 は「制御点 1 個あたり 8 サンプル」を意味し、典型的な制御点数 6..24 で
 * 48..192 点の滑らかな閉曲線となる。テキスト配置の tangent 推定に十分な
 * 密度で、かつ `buildTextPath` の下流二分探索コストが無視できる水準。
 */
const DEFAULT_SMOOTHNESS = 8;

/**
 * RDP 許容誤差を平均辺長に掛ける係数。
 *
 * `epsilon = (100 - followDegree) × avgEdgeLen × RDP_EPSILON_COEFF` で
 * 算出するため、followDegree=0 のとき epsilon = avgEdgeLen（最大簡略化）、
 * followDegree=100 のとき epsilon = 0（簡略化スキップ）となる。0.01 は
 * 「追従度 1 あたり avgEdgeLen × 1% の簡略化許容」という線形感覚値。
 */
const RDP_EPSILON_COEFF = 0.01;

/**
 * 凸包境界からの最大許容深さを平均辺長に掛ける係数。
 *
 * `maxDepth = avgEdgeLen × (followDegree / 100) × CONCAVITY_CLAMP_COEFF` で
 * 算出。followDegree=100 で maxDepth = 2 × avgEdgeLen（ほぼ制限なし）、
 * followDegree=0 で maxDepth = 0（凸包まで引き戻し、実質凸包のみ）。2.0 は
 * 「隣接辺 2 本分の深さまで凹みを許容」する感覚値。
 */
const CONCAVITY_CLAMP_COEFF = 2;

/**
 * Catmull-Rom spline の alpha 値。
 *
 * 0.5 = centripetal。0 = 標準（自己ループしやすい）、1 = chordal（膨らむ）。
 * 詳細はファイル冒頭 JSDoc「なぜ centripetal (alpha=0.5) か」を参照。
 */
const CATMULL_ROM_ALPHA = 0.5;

/**
 * 退化判定の閾値（隣接頂点距離 / 数値ゼロ）。
 *
 * 1e-6 は px 空間での実質ゼロ判定として十分（innerShort = 100+ px スケール）。
 * `offset.ts#DEGEN_EPS` と同値で統一。
 */
const DEGEN_EPS = 1e-6;

/**
 * `minFeatureSize`（≒ fontSize）から RDP epsilon フロアを導く係数。
 *
 * グリフは自身の寸法より小さい曲率半径の形状特徴を物理的に追従できない
 * （剛体ボックスとして描かれるため）。detonate 中の warp はグリフ以下の
 * ジグザグを大量に作るので、`minFeatureSize × 0.5`（グリフ半分）未満の
 * deviation しか持たない特徴を RDP で落とし、文字が追従可能なスケールまで
 * 経路を丸める。2026-07-10 文字被り根本修正の一部。
 */
const FEATURE_FLOOR_COEFF = 0.5;

/**
 * silhouette から text 配置用の独立した path を生成する。
 * silhouette の形状特徴を緩く保持しながら、自己交差のない滑らかな閉曲線を返す。
 *
 * @param silhouette 元の silhouette（warp 後の最終形状、視覚 CW = signedArea > 0 前提）
 * @param followDegree 追従度 0-100（範囲外は clamp）
 * @param smoothness Catmull-Rom spline の補間ステップ数（default 8、負値や非整数は下限 1 に正規化）
 * @param minFeatureSize 経路に残す形状特徴の最小スケール（px、通常は fontSize）。
 *        `minFeatureSize × 0.5` 未満の deviation しか持たない特徴を RDP で落とし、
 *        グリフが物理的に追従できないジグザグ（detonate 中の warp 由来）を
 *        文字スケールで丸める。default 0 = フロアなし（従来挙動、完全後方互換）。
 * @returns text 配置用の滑らかな閉曲線（視覚 CW 保証、頂点数は入力より増える）
 */
export function buildTextBasePath(
  silhouette: Vec2[],
  followDegree: number,
  smoothness?: number,
  minFeatureSize?: number,
): Vec2[] {
  // 退化ガード: 3 頂点未満は面を成さず、補間の意味もないため shallow copy で返す。
  if (silhouette.length < 3) return silhouette.slice();

  // `followDegree` は UI slider 由来の値で範囲外が来る可能性があるため clamp。
  const fd = Math.max(0, Math.min(100, followDegree));
  // `smoothness` は非整数・負値・NaN・undefined をすべて下限 1 に正規化。
  const steps = Math.max(1, Math.floor(smoothness ?? DEFAULT_SMOOTHNESS));
  // フォントサイズ由来の RDP epsilon フロア（FEATURE_FLOOR_COEFF の JSDoc 参照）。
  const featureFloor = Math.max(0, minFeatureSize ?? 0) * FEATURE_FLOOR_COEFF;

  const avgEdgeLen = averageEdgeLength(silhouette);
  const hull = convexHull(silhouette);
  // 全頂点が collinear（面積ゼロ）だと凸包が 2 頂点以下になる。補間できないので原値返却。
  if (hull.length < 3) return silhouette.slice();

  // followDegree → 制御点列の分岐。
  // - 0: 凸包のみ（シンプルな楕円状、最も読みやすい）
  // - 100: silhouette そのまま（元形状に最大追従）。
  //   注（2026-07-10 平滑化）: 一時期ここに featureFloor の RDP を掛けていたが
  //   撤去した。RDP は「残す頂点の離散選択」なので、pulse / detonate の連続
  //   変形中に選択集合が切り替わると spline 形状がフレーム間でポップし、
  //   経路長が非単調に揺れてテキスト全体がガタつく。グリフ未満の
  //   ジグザグ / ヘアピンの除去は下流の `relaxMinRadius`（連続な平滑化）が
  //   一手に担う。
  // - 中間: RDP で代表点を抽出し、凸包境界からの深さを maxDepth で clamp
  let controls: Vec2[];
  if (fd === 0) {
    controls = hull;
  } else if (fd === 100) {
    controls = silhouette.slice();
  } else {
    const epsilon = Math.max((100 - fd) * avgEdgeLen * RDP_EPSILON_COEFF, featureFloor);
    const maxDepth = avgEdgeLen * (fd / 100) * CONCAVITY_CLAMP_COEFF;
    const simplified = simplifyRDP(silhouette, epsilon);
    controls = clampConcavity(simplified, hull, maxDepth);
  }

  // Catmull-Rom 前に隣接重複を除去（knot 計算でゼロ除算を避ける安全側）。
  const deduped = dedupeAdjacent(controls);
  if (deduped.length < 3) return silhouette.slice();

  const smooth = catmullRomClosed(deduped, steps);
  // 巻き向き保証: 凸包 / Catmull-Rom 補間で視覚 CW が崩れた場合の保険。
  // signedArea < 0 なら視覚 CCW なので反転。
  if (signedArea(smooth) < 0) smooth.reverse();
  return smooth;
}

/** `relaxMinRadius` の反復上限デフォルト。リサンプリング後の頂点間隔は
 * `minRadius / 3` なので、半径 minRadius のヘアピンは高々 ~10 頂点の
 * 波長になる。Laplacian 拡散はこのスケールで ~O(波長²) = 100 回で収束
 * するため、200 回は十分な余裕。 */
const RELAX_DEFAULT_ITERATIONS = 200;

/** リサンプリング間隔を `minRadius` から導く除数。間隔 = minRadius/3 で
 * 1 頂点あたりの許容回転 = 間隔/minRadius = 1/3 rad ≈ 19°。 */
const RELAX_SPACING_DIVISOR = 3;

/** ラウンドあたりの turn-clamp sweep 回数。この後にリサンプリングを挟む。 */
const RELAX_SWEEPS_PER_ROUND = 8;

/** 前段の無条件・弱平滑化の回数と強度（連続作用素、閾値なし）。 */
const RELAX_PRE_SMOOTH_SWEEPS = 2;
const RELAX_PRE_SMOOTH_ALPHA = 0.25;

/** turn-clamp の smooth ramp 境界（`turn / maxTurn` 比）。
 * RAMP_LO 以下は動かさず、RAMP_HI 以上で最大強度 0.5。硬い ON/OFF だと
 * 連続変形中に発動集合の切替が経路長ノイズになるため連続移行させる。 */
const RELAX_RAMP_LO = 0.8;
const RELAX_RAMP_HI = 1.2;

/**
 * 閉曲線に最小回転半径を強制する（curvature-limited smoothing）。
 *
 * 背景（2026-07-10 文字被り修正・第 2 弾）:
 * RDP フロア（`buildTextBasePath` の `minFeatureSize`）は偏差の小さい
 * ジグザグしか除去できず、深いスパイク / 谷は制御点として生き残って
 * spline が半径ほぼゼロのヘアピンを作る。曲率半径がグリフ寸法を下回る
 * 経路には、どんな配置アルゴリズムでも文字が内側へ倒れ込んで密着する。
 * 本関数は「文字が乗れる経路の最小条件 = 曲率半径 ≳ fontSize」を
 * 経路側で明示的に強制する。
 *
 * アルゴリズム（決定的）:
 * 1. 経路を弧長一様に間隔 `minRadius / 3` でリサンプリング。
 *    高密度サンプル（~4px）のままだと Laplacian の拡散が波長² に比例して
 *    遅く、半径 minRadius の波長には数千反復が必要になる。先に処理
 *    スケールをグリフ寸法へ合わせるのが収束の要。
 * 2. 各頂点の回転角が `間隔 / minRadius`（rad）を超える頂点だけを
 *    隣接中点へ 50% 引き寄せる（turn-clamp Laplacian）。全頂点が条件を
 *    満たすか `maxIterations` で打ち切り。
 *
 * 縮小系の平滑化なので自己交差を新規に作らない。κ 超過のない頂点には
 * 触れないため、ヘアピン以外の形状（輪郭追従の見た目）は保たれる。
 * 出力の頂点間隔は `minRadius / 3` の粗さになるが、下流の
 * `buildTextPath` が 4px 刻みで再サンプルし、グリフ回転は弦から取る
 * （`textLayout.ts`）ため描画品質への影響はない。
 *
 * @param path 閉曲線（視覚 CW 想定だが巻き向き非依存）。
 * @param minRadius 許容する最小曲率半径（px、通常は fontSize）。
 * @param maxIterations 反復上限（default 200）。
 * @returns 平滑化後の閉曲線（頂点数は `周長 / (minRadius/3)` 程度）。
 *          3 頂点未満・minRadius <= 0 は shallow copy を返す。
 */
export function relaxMinRadius(path: Vec2[], minRadius: number, maxIterations?: number): Vec2[] {
  if (path.length < 3 || !(minRadius > 0)) return path.slice();
  const iters = Math.max(1, Math.floor(maxIterations ?? RELAX_DEFAULT_ITERATIONS));

  const spacing = Math.max(2, minRadius / RELAX_SPACING_DIVISOR);
  // 1 頂点あたりの許容回転角（rad）。間隔 h・半径 R の円弧の頂点回転は h/R。
  const maxTurn = spacing / minRadius;

  // ラウンド制: [一様リサンプリング → 数回の turn-clamp sweep] を繰り返す。
  // 針状突起（needle）は pull で縮むと局所的に頂点が密集し、リサンプリング
  // なしでは Laplacian が停滞する。ラウンドごとに間隔を再均一化することで
  // needle が段階的に短縮 → 消滅し、確実に収束する。
  let pts = resampleClosedUniform(path, spacing);
  if (pts.length < 4) return path.slice();

  // 前段の無条件・弱平滑化（2026-07-10 平滑化）: turn-clamp は「回転角が
  // 閾値を超えた頂点だけ動かす」離散スイッチのため、pulse / detonate の
  // 連続変形中に発動集合が切り替わると経路長がフレーム間で揺れ、テキスト
  // 全体が微振動する。先に全頂点へ一律の弱い Laplacian（連続作用素）を
  // 掛けて高周波を均しておくことで、閾値パスの発動を安定させる。
  // 毎フレーム新しい入力に対する固定深度の適用なので累積収縮はしない。
  for (let s = 0; s < RELAX_PRE_SMOOTH_SWEEPS; s++) {
    pts = laplacianAll(pts, RELAX_PRE_SMOOTH_ALPHA);
  }

  // 反復構造は**固定**（2026-07-10 平滑化）: 「収束したら早期終了」の可変
  // 反復は、連続変形中に実行回数がフレーム間で切り替わって経路長ノイズに
  // なる。毎フレーム同じ回数・同じ順序で適用すれば、作用素全体が入力に
  // 対して（smooth ramp 併用で）連続になる。
  const rounds = Math.max(1, Math.round(iters / RELAX_SWEEPS_PER_ROUND));
  for (let r = 0; r < rounds; r++) {
    for (let s = 0; s < RELAX_SWEEPS_PER_ROUND; s++) {
      pts = relaxOnce(pts, maxTurn);
    }
    pts = resampleClosedUniform(pts, spacing);
    if (pts.length < 4) break;
  }
  return pts;
}

/**
 * 閉曲線を弧長一定間隔でリサンプリングする。
 *
 * **間隔固定・末尾可変**方式（2026-07-10 平滑化）: 先頭（path[0]）から
 * 正確に `spacing` ごとにサンプルし、周長の端数は最後のギャップに吸収する。
 * 旧「個数固定・全体等分」方式は周長のわずかな変化で個数の整数フリップが
 * 起き、**全サンプルの位相が一斉にずれて** relax 後の形状が毎フレーム
 * 揺れていた。間隔固定なら周長変化の影響は末尾ギャップ 1 箇所に閉じ、
 * 連続変形に対してサンプル列が連続に追従する。
 *
 * `relaxMinRadius` の前処理専用。頂点数は最低 8 を保証。
 */
function resampleClosedUniform(path: Vec2[], spacing: number): Vec2[] {
  const N = path.length;
  // 累積弧長を構築。
  const cum: number[] = [0];
  let perimeter = 0;
  for (let i = 0; i < N; i++) {
    const a = path[i];
    const b = path[(i + 1) % N];
    if (!a || !b) {
      cum.push(perimeter);
      continue;
    }
    perimeter += Math.hypot(b[0] - a[0], b[1] - a[1]);
    cum.push(perimeter);
  }
  if (perimeter < DEGEN_EPS) return path.slice();

  // 間隔固定。周長が小さすぎる退化ケースだけ 8 分割へフォールバック。
  const step = perimeter / spacing < 8 ? perimeter / 8 : spacing;
  const out: Vec2[] = [];
  let seg = 0;
  // 末尾ギャップが step の半分未満になる位置までサンプル（極短の最終辺で
  // relax の回転角推定が暴れるのを避ける）。
  const lastTarget = perimeter - step / 2;
  for (let target = 0; target < lastTarget; target += step) {
    // cum は単調増加。前回位置から線形に進めるので全体 O(N + count)。
    while (seg < N - 1 && (cum[seg + 1] ?? perimeter) < target) seg++;
    const a = path[seg % N];
    const b = path[(seg + 1) % N];
    const c0 = cum[seg] ?? 0;
    const c1 = cum[seg + 1] ?? perimeter;
    if (!a || !b) continue;
    const span = c1 - c0;
    const u = span < DEGEN_EPS ? 0 : (target - c0) / span;
    out.push([a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u]);
  }
  return out;
}

/**
 * 全頂点への一律 Laplacian（閾値なしの連続平滑化）。
 * `p_i ← lerp(p_i, (p_{i-1} + p_{i+1})/2, alpha)`。
 */
function laplacianAll(path: Vec2[], alpha: number): Vec2[] {
  const N = path.length;
  const next: Vec2[] = new Array(N);
  for (let i = 0; i < N; i++) {
    const a = path[(i - 1 + N) % N];
    const b = path[i];
    const c = path[(i + 1) % N];
    if (!a || !b || !c) {
      if (b) next[i] = b;
      continue;
    }
    const mx = (a[0] + c[0]) / 2;
    const my = (a[1] + c[1]) / 2;
    next[i] = [b[0] + (mx - b[0]) * alpha, b[1] + (my - b[1]) * alpha];
  }
  return next;
}

/**
 * `relaxMinRadius` の 1 sweep。回転角が大きい頂点を隣接中点へ引き寄せる。
 *
 * 引き寄せ強度は硬い閾値ではなく **smooth ramp**（2026-07-10 平滑化）:
 * `turn / maxTurn` が RAMP_LO 以下で 0、RAMP_HI 以上で最大 0.5、間は線形。
 * ON/OFF の離散スイッチだと連続変形中に発動集合が切り替わって経路長が
 * フレーム間で揺れるため、頂点移動量を幾何の連続関数にする。
 */
function relaxOnce(path: Vec2[], maxTurn: number): Vec2[] {
  const N = path.length;
  const next: Vec2[] = new Array(N);
  for (let i = 0; i < N; i++) {
    const a = path[(i - 1 + N) % N];
    const b = path[i];
    const c = path[(i + 1) % N];
    if (!a || !b || !c) {
      if (b) next[i] = b;
      continue;
    }
    const t1 = Math.atan2(b[1] - a[1], b[0] - a[0]);
    const t2 = Math.atan2(c[1] - b[1], c[0] - b[0]);
    let turn = Math.abs(t2 - t1);
    if (turn > Math.PI) turn = 2 * Math.PI - turn;
    // ramp: ratio ≤ RAMP_LO → 0 / ≥ RAMP_HI → 1、間は線形。
    const ratio = turn / maxTurn;
    const ramp = Math.max(
      0,
      Math.min(1, (ratio - RELAX_RAMP_LO) / (RELAX_RAMP_HI - RELAX_RAMP_LO)),
    );
    if (ramp === 0) {
      next[i] = b;
      continue;
    }
    const alpha = 0.5 * ramp;
    const mx = (a[0] + c[0]) / 2;
    const my = (a[1] + c[1]) / 2;
    next[i] = [b[0] + (mx - b[0]) * alpha, b[1] + (my - b[1]) * alpha];
  }
  return next;
}

/**
 * テキスト経路の起点を「重心から真上への半直線と経路の交点」に回転する
 * （2026-07-10 テキスト動作平滑化 F1）。
 *
 * 旧来の `rotateHullWithCorners`（最上端**頂点**を起点にする）は、pulse /
 * detonate の連続変形中に argmin(y) の頂点が入れ替わった瞬間、経路の
 * pos=0 が離散ジャンプし、リング全体のテキストがスライドして見えた。
 * 半直線との**交点**は形状の連続変形に対して連続に動くため、起点ジャンプが
 * 構造的に消える。「テキストは 12 時位置から読み始まる」という視覚仕様は
 * 維持される。
 *
 * 交点が複数ある場合（上部が凹んでいる等）は最も上（y 最小）を採用。
 * 交点が取れない退化ケースは従来同様の最上端頂点回転にフォールバック。
 *
 * @param path 閉曲線（3 頂点以上）。
 * @returns 交点を先頭に挿入した回転済み頂点列（長さ N+1）。
 */
export function rotateToTopRayAnchor(path: Vec2[]): Vec2[] {
  const N = path.length;
  if (N < 3) return path.slice();

  let cx = 0;
  let cy = 0;
  for (const p of path) {
    cx += p[0];
    cy += p[1];
  }
  cx /= N;
  cy /= N;

  const hit = findTopRayIntersection(path, cx, cy);
  if (hit === null) return rotateToTopVertex(path);
  const { bestY, bestEdge } = hit;

  // [交点, edge+1, ..., edge] の順に並べ替え（進行方向は維持）。
  const out: Vec2[] = [[cx, bestY]];
  const start = (bestEdge + 1) % N;
  for (let k = 0; k < N; k++) {
    const p = path[(start + k) % N];
    if (p) out.push(p);
  }
  return out;
}

/**
 * 垂直線 x = cx と交差する辺のうち、重心より上（Y-down で y < cy）で
 * 最も上の交点（y と辺 index）を返す。該当なしは null。
 */
function findTopRayIntersection(
  path: Vec2[],
  cx: number,
  cy: number,
): { bestY: number; bestEdge: number } | null {
  const N = path.length;
  let bestY = Number.POSITIVE_INFINITY;
  let bestEdge = -1;
  for (let i = 0; i < N; i++) {
    const a = path[i];
    const b = path[(i + 1) % N];
    if (!a || !b) continue;
    const dx = b[0] - a[0];
    if ((a[0] - cx) * (b[0] - cx) > 0) continue; // 両端が同じ側
    if (Math.abs(dx) < DEGEN_EPS) continue; // 垂直辺は隣接辺が拾う
    const t = (cx - a[0]) / dx;
    if (t < 0 || t >= 1) continue;
    const y = a[1] + (b[1] - a[1]) * t;
    if (y > cy) continue; // 下向き側の交点は対象外
    if (y < bestY) {
      bestY = y;
      bestEdge = i;
    }
  }
  return bestEdge < 0 ? null : { bestY, bestEdge };
}

/** フォールバック: 最上端頂点を先頭にした回転（旧挙動と同型）。 */
function rotateToTopVertex(path: Vec2[]): Vec2[] {
  let topIdx = 0;
  for (let i = 1; i < path.length; i++) {
    const p = path[i];
    const t = path[topIdx];
    if (p && t && p[1] < t[1]) topIdx = i;
  }
  return path.slice(topIdx).concat(path.slice(0, topIdx));
}

// ─────────────────────────────────────────────────────────────────────────────
// Private helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * polygon の符号付き面積（Shoelace、視覚 CW で正、視覚 CCW で負）。
 *
 * Y-down 座標系における convention は `silhouette.ts#signedArea` と
 * `offset.ts` の docstring で確定済み（視覚 CW = signedArea > 0）。
 * 本関数は `0.5 × Σ(x_i × y_{i+1} - x_{i+1} × y_i)` をそのまま返す。
 */
function signedArea(path: Vec2[]): number {
  const n = path.length;
  let s = 0;
  for (let i = 0; i < n; i++) {
    const a = path[i];
    const b = path[(i + 1) % n];
    if (!a || !b) continue;
    s += a[0] * b[1] - b[0] * a[1];
  }
  return s * 0.5;
}

/**
 * 全辺長の平均（perimeter / N）。頂点数 0 は 0 を返す。
 *
 * RDP の epsilon と concavity clamp の maxDepth の基準長として使う。
 * silhouette の「普段の辺長」を 1 単位と見なす正規化因子。
 */
function averageEdgeLength(path: Vec2[]): number {
  const n = path.length;
  if (n === 0) return 0;
  let total = 0;
  for (let i = 0; i < n; i++) {
    const a = path[i];
    const b = path[(i + 1) % n];
    if (!a || !b) continue;
    total += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return total / n;
}

/**
 * Andrew's monotone chain で凸包を構築する。
 *
 * 手順:
 * 1. 頂点を (x, y) 辞書順でソート。
 * 2. lower hull を構築（左下→右下、左折で pop）。
 * 3. upper hull を構築（右上→左上、左折で pop）。
 * 4. 両者を連結（それぞれ末尾は共有点なので除外）。
 *
 * **巻き向き**: 数学座標系では CCW を返す標準アルゴリズムだが、本プロジェクトは
 * Y-down 画面座標系のため「数学 CCW = 視覚 CW」となる（Y 軸反転で巻きが反転
 * するため）。よって戻り値は視覚 CW = signedArea > 0 となり本プロジェクト規約
 * と整合する。
 *
 * **エッジケース**:
 * - 重複点: ソート後に前後一致するが、cross=0 で pop されるので結果的に除外。
 * - 全点 collinear: lower hull のみで全点列、upper hull も同じで重複除外後
 *   頂点数 2 以下となる。呼び出し側で `hull.length < 3` を検知して fallback。
 *
 * @param points 入力頂点列（3 点以上）。
 * @returns 凸包頂点列（視覚 CW、末尾と先頭は重複しない）。
 */
function convexHull(points: Vec2[]): Vec2[] {
  const n = points.length;
  if (n < 3) return points.slice();

  // ソート用にシャローコピー（入力は変更しない）。
  const sorted = points.slice().sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]));

  // lower hull: 左下 → 右下 方向、`<= 0` で直線上の中間点も除外。
  const lower = buildHullChain(sorted);
  // upper hull: 逆順走査で右上 → 左上 方向。
  const upper = buildHullChain(sorted.slice().reverse());

  // 末尾は lower の先頭と upper の先頭に一致するため除外して結合。
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/**
 * Andrew's monotone chain の 1 方向分の hull chain を構築する。
 *
 * `sorted` を順番に見て stack に push。stack 末尾 2 点と新点の cross が
 * 左折/直線（`<= 0`）の場合は末尾を pop して凸包内側の点を除外する。
 *
 * lower / upper 両方で同じアルゴリズムを使い、呼び出し側で sorted を reverse
 * することで 2 方向を実現する。
 */
function buildHullChain(sorted: Vec2[]): Vec2[] {
  const chain: Vec2[] = [];
  for (const p of sorted) {
    popWhileNonRightTurn(chain, p);
    chain.push(p);
  }
  return chain;
}

/**
 * `chain` の末尾 2 点と `p` で成す turn が右折でない（cross <= 0）間、
 * 末尾を pop する。Andrew's monotone chain の核となる操作。
 */
function popWhileNonRightTurn(chain: Vec2[], p: Vec2): void {
  while (chain.length >= 2) {
    const a = chain[chain.length - 2];
    const b = chain[chain.length - 1];
    if (!a || !b) break;
    // cross = (b-a) × (p-b)。正なら左折、負なら右折、0 なら直線。
    // 数学座標系の左折は Y-down 視覚で右折に見えることに留意（convexHull docstring 参照）。
    const cross = (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
    if (cross <= 0) chain.pop();
    else break;
  }
}

/**
 * 点 `p` から線分 `[a, b]` への最短距離と垂線の足を返す。
 *
 * パラメトリック `q(t) = a + t × (b - a)` を `t ∈ [0, 1]` で clamp し、
 * 線分端点外のときは端点を foot とする。退化線分（a ≒ b）は `a` を foot
 * として扱う。
 *
 * RDP と concavity clamp の両方から使う汎用 helper。
 */
function pointToSegmentDistance(p: Vec2, a: Vec2, b: Vec2): { d: number; foot: Vec2 } {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  // 退化線分（a ≒ b）は a を foot として距離 |p - a| を返す。
  if (lenSq < DEGEN_EPS) {
    const ex = p[0] - a[0];
    const ey = p[1] - a[1];
    return { d: Math.hypot(ex, ey), foot: [a[0], a[1]] };
  }
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq;
  // 端点外は端点まで clamp（足は線分内に収める）。
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const fx = a[0] + dx * t;
  const fy = a[1] + dy * t;
  return { d: Math.hypot(p[0] - fx, p[1] - fy), foot: [fx, fy] };
}

/**
 * 閉曲線版 Ramer-Douglas-Peucker 簡略化。
 *
 * 閉曲線に RDP をそのまま適用すると始終点が固定され偏るため、凸包頂点内で
 * 互いに最も離れた 2 点を選んで分割し、2 本の open path に RDP を独立適用
 * してから結合する。これにより「どの方向から見ても等しい簡略化」が得られる。
 *
 * `epsilon <= 0` のとき簡略化せず shallow copy を返す（followDegree=100 経路は
 * 呼び出し側で別分岐だが、念のため）。
 *
 * @param path 入力閉曲線（3 頂点以上）。
 * @param epsilon 許容誤差（px）。これ以下の deviation の点は除去される。
 */
function simplifyRDP(path: Vec2[], epsilon: number): Vec2[] {
  if (path.length < 3 || epsilon <= 0) return path.slice();

  const [iA, iB] = findFarthestPair(path);
  const seg1 = sliceRange(path, iA, iB);
  const seg2 = sliceWrapRange(path, iB, iA);

  const simp1 = rdpOpen(seg1, epsilon);
  const simp2 = rdpOpen(seg2, epsilon);
  // 結合: simp1 の末尾は iB 頂点、simp2 の先頭も iB 頂点で重複。
  // simp2 の末尾も iA 頂点で simp1 先頭と重複するため両端の重複を除外。
  return simp1.concat(simp2.slice(1, -1));
}

/**
 * 入力 path の中から **互いに最も離れた 2 点** の index ペアを返す。
 *
 * 閉曲線 RDP の 2 分割基準として、凸包 diameter に相当する最遠ペアを取る。
 * O(N²) の総当たりだが典型 N=60..200 で 40000 比較程度、text path 構築は
 * 毎フレームではなく再構築タイミングのみなので十分許容範囲。
 *
 * 凸包 rotating calipers を使えば O(N) になるが、実装コストと退化ケースの
 * 罠（平行辺での undefined 挙動）を避けるため明示的な O(N²) を採用。
 */
function findFarthestPair(path: Vec2[]): [number, number] {
  let iA = 0;
  let iB = 1;
  let maxD2 = -1;
  const N = path.length;
  for (let i = 0; i < N; i++) {
    const a = path[i];
    if (!a) continue;
    for (let j = i + 1; j < N; j++) {
      const b = path[j];
      if (!b) continue;
      const d2 = (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2;
      if (d2 > maxD2) {
        maxD2 = d2;
        iA = i;
        iB = j;
      }
    }
  }
  return [iA, iB];
}

/**
 * path[from..to]（両端含む）をそのまま抽出する（from <= to 前提）。
 */
function sliceRange(path: Vec2[], from: number, to: number): Vec2[] {
  const out: Vec2[] = [];
  for (let i = from; i <= to; i++) {
    const p = path[i];
    if (p) out.push(p);
  }
  return out;
}

/**
 * path[from..N-1] と path[0..to] を連結（閉曲線を `from` から `to` まで巡る）。
 *
 * 閉曲線 RDP で `iB → iA` 側の開セグメントを取り出すために使用。
 */
function sliceWrapRange(path: Vec2[], from: number, to: number): Vec2[] {
  const out: Vec2[] = [];
  const N = path.length;
  for (let i = from; i < N; i++) {
    const p = path[i];
    if (p) out.push(p);
  }
  for (let i = 0; i <= to; i++) {
    const p = path[i];
    if (p) out.push(p);
  }
  return out;
}

/**
 * Open path に対する RDP 簡略化（iterative stack 版、再帰深さ対策）。
 *
 * 標準的な Douglas-Peucker: 両端を結ぶ線分から最も離れた点を見つけ、
 * その deviation が epsilon 以上なら分割して再帰、未満なら両端のみ残す。
 * 本実装は stack で明示管理し、長いパスでの stack overflow を防ぐ。
 *
 * 戻り値は「残す index」を昇順で埋めた配列を基に path から値を取り出す。
 * 両端（先頭 / 末尾）は常に残す。
 */
function rdpOpen(path: Vec2[], epsilon: number): Vec2[] {
  const n = path.length;
  if (n < 3) return path.slice();

  // `keep[i] = true` なら index i の点を残す。両端は必須。
  const keep = new Array<boolean>(n).fill(false);
  keep[0] = true;
  keep[n - 1] = true;

  // stack は [startIdx, endIdx] の区間ペアを保持。初期は全区間。
  const stack: Array<[number, number]> = [[0, n - 1]];
  while (stack.length > 0) {
    const top = stack.pop();
    if (!top) break;
    processRdpSegment(path, top[0], top[1], epsilon, keep, stack);
  }

  return collectKept(path, keep);
}

/**
 * 1 区間 `[s, e]` の RDP 処理: 最大 deviation を持つ中間点を見つけて
 * epsilon 以上なら `keep` に記録し、両側の区間を stack に push する。
 *
 * `rdpOpen` の内ループから切り出した per-segment 処理。iterative 実装の
 * 核心部分で、再帰版より深いパスでも stack overflow しない利点がある。
 */
function processRdpSegment(
  path: Vec2[],
  s: number,
  e: number,
  epsilon: number,
  keep: boolean[],
  stack: Array<[number, number]>,
): void {
  if (e - s < 2) return;
  const a = path[s];
  const b = path[e];
  if (!a || !b) return;

  const maxIdx = findMaxDeviationIndex(path, s, e, a, b, epsilon);
  if (maxIdx > 0) {
    keep[maxIdx] = true;
    stack.push([s, maxIdx]);
    stack.push([maxIdx, e]);
  }
}

/**
 * 区間 `[s+1, e-1]` の中から、線分 `[a, b]` への距離が `epsilon` を超え、
 * かつ最大となる index を返す。該当なしは `-1`。
 */
function findMaxDeviationIndex(
  path: Vec2[],
  s: number,
  e: number,
  a: Vec2,
  b: Vec2,
  epsilon: number,
): number {
  let maxD = epsilon;
  let maxIdx = -1;
  for (let i = s + 1; i < e; i++) {
    const p = path[i];
    if (!p) continue;
    const { d } = pointToSegmentDistance(p, a, b);
    if (d > maxD) {
      maxD = d;
      maxIdx = i;
    }
  }
  return maxIdx;
}

/**
 * `keep[i] === true` な index の点のみを path から順番に抽出する。
 */
function collectKept(path: Vec2[], keep: boolean[]): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < path.length; i++) {
    if (keep[i]) {
      const p = path[i];
      if (p) out.push(p);
    }
  }
  return out;
}

/**
 * 各頂点について、凸包境界からの距離が `maxDepth` を超える場合、
 * 最近接凸包辺の足（foot）へ射影して引き戻す。
 *
 * 「silhouette の凹みを許容しつつも深すぎる凹みは text の可読性を損なう」
 * という定性要件に対応。followDegree が小さいほど maxDepth も小さくなり、
 * より多くの頂点が凸包側へ引き戻される。
 *
 * 凸包内外の判定は行わず、単純に「各凸包辺への点-線分距離の最小値」を
 * その頂点の深さとして使う（凸包内の点は全辺から距離 > 0 だが、これも
 * 単純に「凸包境界からどれだけ引っ込んでいるか」として扱う）。
 *
 * @param path 入力頂点列（簡略化後）。
 * @param hull 凸包（視覚 CW）。
 * @param maxDepth 許容深さ（px）。この値を超えた深さの頂点のみ射影。
 * @returns 深さ clamp 後の頂点列（長さは入力と同じ）。
 */
function clampConcavity(path: Vec2[], hull: Vec2[], maxDepth: number): Vec2[] {
  const hN = hull.length;
  if (hN < 3) return path.slice();

  return path.map((p): Vec2 => {
    // 各凸包辺への最短距離と足を求め、最小距離の辺の足を採用。
    let bestD = Number.POSITIVE_INFINITY;
    let bestFoot: Vec2 = [p[0], p[1]];
    for (let i = 0; i < hN; i++) {
      const a = hull[i];
      const b = hull[(i + 1) % hN];
      if (!a || !b) continue;
      const { d, foot } = pointToSegmentDistance(p, a, b);
      if (d < bestD) {
        bestD = d;
        bestFoot = foot;
      }
    }
    // 許容深さ以内ならそのまま、超えていたら foot（凸包境界上の最近接点）に引き戻し。
    if (bestD <= maxDepth) return [p[0], p[1]];
    return bestFoot;
  });
}

/**
 * 隣接頂点が `DEGEN_EPS` 未満の距離で重複している場合に後者を除去する。
 *
 * Catmull-Rom の centripetal knot 計算では隣接距離の sqrt を取るため、
 * 距離ゼロの点があると t_next === t_prev となり補間で 0/0 が発生する。
 * 本関数は事前に重複を除去して安全性を担保する。
 *
 * 閉曲線なので末尾 vs 先頭の重複もチェックして末尾を除去する。
 */
function dedupeAdjacent(path: Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const p of path) {
    const last = out[out.length - 1];
    if (!last) {
      out.push(p);
      continue;
    }
    if (Math.hypot(p[0] - last[0], p[1] - last[1]) < DEGEN_EPS) continue;
    out.push(p);
  }
  // 閉曲線: 末尾と先頭が重複していたら末尾を削除。
  const first = out[0];
  const tail = out[out.length - 1];
  if (first && tail && out.length > 1) {
    if (Math.hypot(first[0] - tail[0], first[1] - tail[1]) < DEGEN_EPS) {
      out.pop();
    }
  }
  return out;
}

/**
 * 閉曲線に対する Centripetal Catmull-Rom spline 補間。
 *
 * 各 segment は制御点 4 点 `p0, p1, p2, p3` を使い、`p1 → p2` の区間を
 * `steps` 等分してサンプル点を生成する。閉曲線なので index は
 * `(k + N) % N` で wrap し、全 N セグメント分を連結する。
 *
 * **Centripetal (alpha=0.5) の knot 計算**:
 * `t_{i+1} = t_i + |p_{i+1} - p_i|^alpha`
 * これにより制御点間距離の不均等に対して自己ループを作らず、
 * 視覚的な滑らかさと形状保持のバランスが取れる。
 *
 * **segment 連結**:
 * 各 segment で `s = 0..steps-1` を生成し `s === steps` は次 segment の
 * 始点と重複するため除外する。結果として総頂点数 = `N × steps`。
 *
 * @param controls 制御点列（3 頂点以上、隣接重複なし前提）。
 * @param steps 1 segment あたりの分割数（1 以上）。
 * @returns 補間後の閉曲線（頂点数 = controls.length × steps）。
 */
function catmullRomClosed(controls: Vec2[], steps: number): Vec2[] {
  const N = controls.length;
  if (N < 3) return controls.slice();

  const out: Vec2[] = [];
  for (let i = 0; i < N; i++) {
    // 閉曲線 wrap: p0 = p[i-1], p1 = p[i], p2 = p[i+1], p3 = p[i+2]。
    const p0 = controls[(i - 1 + N) % N];
    const p1 = controls[i];
    const p2 = controls[(i + 1) % N];
    const p3 = controls[(i + 2) % N];
    if (!p0 || !p1 || !p2 || !p3) continue;
    appendCatmullRomSegment(out, p0, p1, p2, p3, steps);
  }
  return out;
}

/**
 * 1 segment 分（p1 → p2）の centripetal Catmull-Rom 補間点を `out` に push。
 *
 * knot `t0..t3` は centripetal (alpha=0.5) で計算。補間式は de Boor / Barry-Goldman
 * 形式で `A1, A2, A3` → `B1, B2` → `C` と段階的に線形補間する。
 *
 * `t === t_prev` となる退化ケース（隣接距離ゼロ）は `dedupeAdjacent` で
 * 事前に除去済み前提だが、安全側として分母が DEGEN_EPS 未満なら p1 を
 * そのまま返す保険を入れる。
 */
function appendCatmullRomSegment(
  out: Vec2[],
  p0: Vec2,
  p1: Vec2,
  p2: Vec2,
  p3: Vec2,
  steps: number,
): void {
  // centripetal knot: t_{i+1} = t_i + |p_{i+1} - p_i|^alpha
  const t0 = 0;
  const t1 = t0 + Math.hypot(p1[0] - p0[0], p1[1] - p0[1]) ** CATMULL_ROM_ALPHA;
  const t2 = t1 + Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) ** CATMULL_ROM_ALPHA;
  const t3 = t2 + Math.hypot(p3[0] - p2[0], p3[1] - p2[1]) ** CATMULL_ROM_ALPHA;

  // 退化: knot が等しいと補間で 0/0 になる。先頭のみ push して抜ける。
  if (t2 - t1 < DEGEN_EPS || t1 - t0 < DEGEN_EPS || t3 - t2 < DEGEN_EPS) {
    out.push([p1[0], p1[1]]);
    return;
  }

  for (let s = 0; s < steps; s++) {
    const t = t1 + ((t2 - t1) * s) / steps;
    out.push(barryGoldman(p0, p1, p2, p3, t0, t1, t2, t3, t));
  }
}

/**
 * Barry-Goldman 形式の centripetal Catmull-Rom 評価。
 *
 * 3 段線形補間:
 *   A1 = lerp(p0, p1, (t-t0)/(t1-t0))
 *   A2 = lerp(p1, p2, (t-t1)/(t2-t1))
 *   A3 = lerp(p2, p3, (t-t2)/(t3-t2))
 *   B1 = lerp(A1, A2, (t-t0)/(t2-t0))
 *   B2 = lerp(A2, A3, (t-t1)/(t3-t1))
 *   C  = lerp(B1, B2, (t-t1)/(t2-t1))
 *
 * 40 行目安に合わせて `appendCatmullRomSegment` から切り出した pure helper。
 */
function barryGoldman(
  p0: Vec2,
  p1: Vec2,
  p2: Vec2,
  p3: Vec2,
  t0: number,
  t1: number,
  t2: number,
  t3: number,
  t: number,
): Vec2 {
  const a1 = lerpVec(p0, p1, (t - t0) / (t1 - t0));
  const a2 = lerpVec(p1, p2, (t - t1) / (t2 - t1));
  const a3 = lerpVec(p2, p3, (t - t2) / (t3 - t2));
  const b1 = lerpVec(a1, a2, (t - t0) / (t2 - t0));
  const b2 = lerpVec(a2, a3, (t - t1) / (t3 - t1));
  return lerpVec(b1, b2, (t - t1) / (t2 - t1));
}

/**
 * 2 点を `t` で線形補間する（`t=0 → a`, `t=1 → b`）。
 *
 * `barryGoldman` 内から多用するため薄い helper として切り出し。
 */
function lerpVec(a: Vec2, b: Vec2, t: number): Vec2 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}
